import { cur } from '@/lib/currency';
import { useEffect, useState, useMemo } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Sparkles, Play, RefreshCw, Edit, ChevronDown, ChevronRight, AlertCircle, Check, Clock, Save, Plus, X, Lightbulb } from 'lucide-react';
import { LineChart, Line, ResponsiveContainer, Tooltip as ReTooltip, XAxis, YAxis, Bar, BarChart } from 'recharts';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';

const fmtSR = (n) => `${cur()} ${Number(n || 0).toLocaleString('en-SA', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const fmtDate = (iso) => (iso || '').slice(0, 10);

/**
 * Daily Reconciliation dashboard (iter506).
 *
 * Surfaces the cron's output as a tangible UI:
 *  • Sparkline of rows-converted/day + amount/day for the last 30 runs.
 *  • Per-run drilldown with per-preset breakdown.
 *  • "Edit preset" button on any preset that matched 0 rows in the
 *    most recent run — quick path to refine the signature.
 *  • "Run Now" button to fire the cron on demand.
 */
export default function DailyReconciliationPage() {
  const [runs, setRuns] = useState([]);
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [editPreset, setEditPreset] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', signature: '', category: '' });
  const [editBusy, setEditBusy] = useState(false);
  // iter507 — Schedule + Preset Health state
  const [schedule, setSchedule] = useState({ mode: 'fixed', fixed_time: '09:00', interval_hours: 6, last_auto_run_at: null, slack_webhook_url: '' });
  const [scheduleBusy, setScheduleBusy] = useState(false);
  const [scheduleDirty, setScheduleDirty] = useState(false);
  const [health, setHealth] = useState({ presets: [], days: 120 });
  // iter508 — Recently-archived dormant presets for the undo banner.
  const [recentlyArchived, setRecentlyArchived] = useState([]);
  const [undoBusy, setUndoBusy] = useState(false);
  // iter509 — Auto-suggester state.
  const [suggestions, setSuggestions] = useState([]);
  const [suggestCreate, setSuggestCreate] = useState(null); // signature object being turned into preset
  const [suggestForm, setSuggestForm] = useState({ name: '', kind: 'expense', category: '' });
  const [suggestBusy, setSuggestBusy] = useState(false);
  // iter513 — Bulk-create state (checkboxes on suggestion cards + batch button).
  const [bulkSelected, setBulkSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkPanel, setBulkPanel] = useState(null); // {kind, category} for batch defaults

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [r, p, s, h, a, sg] = await Promise.all([
        api.get('/daily-reconciliation/runs', { params: { limit: 30 } }),
        api.get('/bulk-convert-presets'),
        api.get('/daily-reconciliation/settings'),
        api.get('/daily-reconciliation/preset-health', { params: { days: 120 } }),
        api.get('/bulk-convert-presets/recently-archived'),
        api.get('/daily-reconciliation/preset-suggestions', { params: { days: 190, min_count: 3 } }),
      ]);
      setRuns(r.data?.runs || []);
      setPresets(p.data?.presets || []);
      setSchedule({
        mode: s.data?.mode || 'fixed',
        fixed_time: s.data?.fixed_time || '09:00',
        interval_hours: s.data?.interval_hours || 6,
        last_auto_run_at: s.data?.last_auto_run_at || null,
        slack_webhook_url: s.data?.slack_webhook_url || '',
      });
      setScheduleDirty(false);
      setHealth({ presets: h.data?.presets || [], days: h.data?.days || 120 });
      setRecentlyArchived(a.data?.presets || []);
      setSuggestions(sg.data?.suggestions || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load');
    } finally { setLoading(false); }
  };

  const undoAllArchived = async () => {
    setUndoBusy(true);
    try {
      const r = await api.post('/bulk-convert-presets/restore-all-recent');
      toast.success(`Restored ${r.data?.restored || 0} preset${(r.data?.restored || 0) === 1 ? '' : 's'}`);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Undo failed');
    } finally { setUndoBusy(false); }
  };

  const undoOne = async (p) => {
    try {
      await api.post(`/bulk-convert-presets/${p.id}/restore`);
      toast.success(`Restored "${p.name}"`);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Restore failed');
    }
  };

  // iter509 — Auto-suggester handlers.
  const openSuggestCreate = (sg) => {
    setSuggestCreate(sg);
    setSuggestForm({
      name: `Auto: ${sg.signature}`,
      kind: 'expense',
      category: '',
    });
  };

  const dismissSuggestion = async (sg) => {
    try {
      await api.post('/daily-reconciliation/preset-suggestions/dismiss', { signature: sg.signature });
      toast.success(`Dismissed "${sg.signature}"`);
      setSuggestions((prev) => prev.filter((s) => s.signature !== sg.signature));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Dismiss failed');
    }
  };

  const submitSuggestCreate = async () => {
    if (!suggestCreate) return;
    if (!suggestForm.name.trim()) { toast.error('Name is required'); return; }
    if (suggestForm.kind === 'expense' && !suggestForm.category.trim()) {
      toast.error('Category is required for expense kind'); return;
    }
    setSuggestBusy(true);
    try {
      const payload = {
        name: suggestForm.name.trim(),
        match_by: 'signature',
        signature: suggestCreate.signature,
        kind: suggestForm.kind,
        defaults: {},
        include_anchor: true,
      };
      if (suggestForm.kind === 'expense') {
        payload.defaults.category = suggestForm.category.trim();
      }
      await api.post('/bulk-convert-presets', payload);
      toast.success(`Preset "${suggestForm.name}" created`);
      setSuggestCreate(null);
      // Optimistically dismiss so it doesn't reappear before refetch.
      setSuggestions((prev) => prev.filter((s) => s.signature !== suggestCreate.signature));
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create preset');
    } finally { setSuggestBusy(false); }
  };

  // iter513 — Bulk-create handlers.
  const toggleBulkSelected = (sig) => {
    setBulkSelected((prev) => {
      const next = new Set(prev);
      if (next.has(sig)) next.delete(sig); else next.add(sig);
      return next;
    });
  };

  const selectAllVisible = () => {
    setBulkSelected(new Set(suggestions.slice(0, 12).map((s) => s.signature)));
  };

  const clearBulkSelected = () => setBulkSelected(new Set());

  const openBulkPanel = () => {
    if (bulkSelected.size === 0) {
      toast.error('Select at least one pattern first');
      return;
    }
    setBulkPanel({ kind: 'expense', category: '' });
  };

  const submitBulkCreate = async () => {
    if (!bulkPanel) return;
    const kind = bulkPanel.kind || 'expense';
    const cat = (bulkPanel.category || '').trim();
    if (kind === 'expense' && !cat) { toast.error('Category is required for kind=expense'); return; }
    const chosen = suggestions.filter((s) => bulkSelected.has(s.signature));
    if (chosen.length === 0) { toast.error('Nothing selected'); return; }
    setBulkBusy(true);
    try {
      const payload = {
        presets: chosen.map((sg) => ({
          name: `Auto: ${sg.signature}`,
          match_by: 'signature',
          signature: sg.signature,
          kind,
          defaults: kind === 'expense' ? { category: cat } : {},
          include_anchor: true,
        })),
      };
      const r = await api.post('/bulk-convert-presets/bulk-create', payload);
      const created = r.data?.created || 0;
      const failed = (r.data?.failed || []).length;
      if (failed > 0) {
        toast.warning(`Created ${created} preset${created === 1 ? '' : 's'} — ${failed} failed (see console)`);
        console.warn('bulk-create failures:', r.data?.failed);
      } else {
        toast.success(`Created ${created} preset${created === 1 ? '' : 's'}`);
      }
      // Optimistically drop the created sigs from the visible suggestions.
      setSuggestions((prev) => prev.filter((s) => !bulkSelected.has(s.signature)));
      clearBulkSelected();
      setBulkPanel(null);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk create failed');
    } finally { setBulkBusy(false); }
  };


  const saveSchedule = async () => {
    setScheduleBusy(true);
    try {
      const payload = { mode: schedule.mode };
      if (schedule.mode === 'fixed') payload.fixed_time = schedule.fixed_time;
      if (schedule.mode === 'interval') payload.interval_hours = schedule.interval_hours;
      payload.slack_webhook_url = schedule.slack_webhook_url || '';
      await api.put('/daily-reconciliation/settings', payload);
      toast.success('Schedule saved');
      setScheduleDirty(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setScheduleBusy(false); }
  };

  // iter516 — Verify the tenant Slack webhook
  const testSlack = async () => {
    try {
      const r = await api.post('/daily-reconciliation/test-slack', {});
      if (r.data?.ok) toast.success(r.data.message || 'Slack reached!');
      else toast.warning(r.data?.message || 'Slack test failed');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Slack test failed');
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await api.post('/daily-reconciliation/run-now', {});
      const c = r.data?.rows_converted || 0;
      toast.success(
        c === 0
          ? 'No new rows matched any preset right now'
          : `Auto-classified ${c} row${c === 1 ? '' : 's'} · ${cur()} ${(r.data?.total_amount || 0).toLocaleString()}`
      );
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Run failed');
    } finally { setRunning(false); }
  };

  // Sparkline data: latest run per day (assumes 1 cron run/day).
  const sparkData = useMemo(() => {
    const byDay = {};
    [...runs].reverse().forEach((r) => {
      const day = fmtDate(r.ran_at);
      if (!day) return;
      byDay[day] = byDay[day] || { day, rows: 0, amount: 0 };
      byDay[day].rows += r.rows_converted || 0;
      byDay[day].amount += r.total_amount || 0;
    });
    return Object.values(byDay).slice(-30);
  }, [runs]);

  const totalRows = sparkData.reduce((s, d) => s + d.rows, 0);
  const totalAmount = sparkData.reduce((s, d) => s + d.amount, 0);
  const lastRun = runs[0];

  // Presets that matched 0 rows in the latest run → highlighted for editing.
  const zeroMatchPresetIds = useMemo(() => {
    if (!lastRun?.per_preset) return new Set();
    return new Set(lastRun.per_preset.filter((p) => p.converted === 0).map((p) => p.id));
  }, [lastRun]);

  const openEdit = (preset) => {
    setEditPreset(preset);
    setEditForm({
      name: preset.name || '',
      signature: preset.signature || '',
      category: preset.defaults?.category || '',
    });
  };

  const submitEdit = async () => {
    if (!editPreset) return;
    setEditBusy(true);
    try {
      const payload = { name: editForm.name };
      if (editPreset.match_by === 'signature') payload.signature = editForm.signature;
      if (editPreset.kind === 'expense') {
        payload.defaults = { ...(editPreset.defaults || {}), category: editForm.category };
      }
      await api.put(`/bulk-convert-presets/${editPreset.id}`, payload);
      toast.success('Preset updated');
      setEditPreset(null);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    } finally { setEditBusy(false); }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-[1400px] mx-auto" data-testid="daily-recon-page">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-lg shadow-indigo-200/50">
              <Sparkles size={22} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" data-testid="daily-recon-title">Daily Reconciliation</h1>
              <p className="text-sm text-muted-foreground mt-0.5">Cron at 09:00 local · last 30 runs · {presets.length} saved preset{presets.length === 1 ? '' : 's'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={fetchAll} disabled={loading} data-testid="recon-refresh">
              <RefreshCw size={14} className={`mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <Button size="sm" onClick={runNow} disabled={running || presets.length === 0} className="bg-indigo-600 hover:bg-indigo-700" data-testid="recon-run-now">
              <Play size={14} className="mr-1" /> {running ? 'Running…' : 'Run now'}
            </Button>
          </div>
        </div>

        {/* iter508 — Auto-archive undo banner. Shows for 7 days after
            a dormant-cleanup auto-archives presets. Single-click
            restores ALL recent archives; per-row Restore handles
            partial restoration. */}
        {recentlyArchived.length > 0 && (
          <Card className="border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50/40" data-testid="undo-banner">
            <CardContent className="p-4">
              <div className="flex items-start gap-3 flex-wrap">
                <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
                  <AlertCircle size={16} className="text-amber-700" />
                </div>
                <div className="flex-1 min-w-[260px]">
                  <div className="text-sm font-bold" data-testid="undo-banner-title">
                    {recentlyArchived.length} preset{recentlyArchived.length === 1 ? '' : 's'} auto-archived (60d of 0% activity)
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-0.5">
                    Restore within 7 days if any of these should keep running. After that they stay archived but are never permanently deleted — you can find them in the archive collection.
                  </p>
                  <div className="mt-2 flex items-center gap-1 flex-wrap">
                    {recentlyArchived.slice(0, 4).map((p) => (
                      <Badge key={p.id} variant="outline" className="text-[10px] border-amber-300 bg-white" data-testid={`archived-pill-${p.id}`}>
                        {p.name}
                        <button
                          onClick={() => undoOne(p)}
                          className="ml-1 text-amber-700 hover:text-amber-900 font-bold"
                          data-testid={`archived-undo-${p.id}`}
                          title="Restore just this preset"
                        >
                          ↺
                        </button>
                      </Badge>
                    ))}
                    {recentlyArchived.length > 4 && (
                      <span className="text-[10px] text-muted-foreground italic">+{recentlyArchived.length - 4} more</span>
                    )}
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={undoAllArchived}
                  disabled={undoBusy}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="undo-all-btn"
                >
                  {undoBusy ? 'Restoring…' : `Undo all (${recentlyArchived.length})`}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* iter507 — Schedule controls */}
        <Card data-testid="schedule-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Clock size={14} className="text-indigo-600" />
              <h3 className="text-sm font-bold">Auto-Reconcile Schedule</h3>
              <Badge variant="outline" className="text-[10px] ml-2" data-testid="schedule-current-mode">
                Currently: {schedule.mode === 'fixed' ? `daily at ${schedule.fixed_time} UTC` : schedule.mode === 'interval' ? `every ${schedule.interval_hours}h` : 'disabled'}
              </Badge>
              {schedule.last_auto_run_at && (
                <span className="text-[10px] text-muted-foreground ml-auto">Last auto-run: {(schedule.last_auto_run_at || '').slice(0, 16).replace('T', ' ')}</span>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
              <div>
                <Label>Mode</Label>
                <Select value={schedule.mode} onValueChange={(v) => { setSchedule((s) => ({ ...s, mode: v })); setScheduleDirty(true); }}>
                  <SelectTrigger data-testid="schedule-mode-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fixed" data-testid="schedule-mode-fixed">Fixed time daily</SelectItem>
                    <SelectItem value="interval" data-testid="schedule-mode-interval">Every N hours</SelectItem>
                    <SelectItem value="disabled" data-testid="schedule-mode-disabled">Disabled (manual only)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {schedule.mode === 'fixed' && (
                <div>
                  <Label>Fixed time (HH:MM UTC)</Label>
                  <Input
                    type="time"
                    value={schedule.fixed_time}
                    onChange={(e) => { setSchedule((s) => ({ ...s, fixed_time: e.target.value })); setScheduleDirty(true); }}
                    data-testid="schedule-fixed-time"
                  />
                </div>
              )}
              {schedule.mode === 'interval' && (
                <div>
                  <Label>Every N hours (1-24)</Label>
                  <Input
                    type="number"
                    min="1"
                    max="24"
                    value={schedule.interval_hours}
                    onChange={(e) => { setSchedule((s) => ({ ...s, interval_hours: parseInt(e.target.value || '1', 10) })); setScheduleDirty(true); }}
                    data-testid="schedule-interval-hours"
                  />
                </div>
              )}
              <div className="md:col-span-2 flex items-end gap-2">
                <Button
                  size="sm"
                  onClick={saveSchedule}
                  disabled={!scheduleDirty || scheduleBusy}
                  data-testid="schedule-save"
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  <Save size={12} className="mr-1" /> {scheduleBusy ? 'Saving…' : 'Save schedule'}
                </Button>
                {schedule.mode === 'disabled' && (
                  <p className="text-[10px] text-amber-600">Cron won't run automatically — use "Run now" above.</p>
                )}
              </div>
            </div>

            {/* iter516 — per-tenant Slack webhook for daily digest */}
            <div className="mt-4 pt-3 border-t">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Slack notifications (optional)</span>
                {schedule.slack_webhook_url && (
                  <Badge variant="outline" className="text-[10px] border-emerald-300 text-emerald-700 bg-emerald-50">Configured</Badge>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-2 items-center">
                <Input
                  type="url"
                  placeholder="https://hooks.slack.com/services/T.../B.../..."
                  value={schedule.slack_webhook_url || ''}
                  onChange={(e) => { setSchedule((s) => ({ ...s, slack_webhook_url: e.target.value })); setScheduleDirty(true); }}
                  className="text-xs font-mono"
                  data-testid="schedule-slack-url"
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={testSlack}
                  disabled={!schedule.slack_webhook_url || scheduleDirty}
                  data-testid="schedule-slack-test"
                  title={scheduleDirty ? 'Save first, then test' : 'Send a test ping to verify the webhook'}
                >
                  Test
                </Button>
                {schedule.slack_webhook_url && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-muted-foreground"
                    onClick={() => { setSchedule((s) => ({ ...s, slack_webhook_url: '' })); setScheduleDirty(true); }}
                    data-testid="schedule-slack-clear"
                  >Clear</Button>
                )}
              </div>
              <p className="text-[10px] text-muted-foreground mt-1.5">
                Paste an Incoming Webhook URL from Slack to receive a message on every daily reconciliation run. Leave empty to disable.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Sparklines */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card data-testid="spark-rows-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Rows · last 30 days</div>
                  <div className="text-3xl font-bold tabular-nums" data-testid="spark-rows-total">{totalRows.toLocaleString()}</div>
                </div>
                <Badge variant="outline" className="text-[10px]">{sparkData.length} run{sparkData.length === 1 ? '' : 's'}</Badge>
              </div>
              <div className="h-24">
                {sparkData.length > 0 ? (
                  <ResponsiveContainer>
                    <LineChart data={sparkData}>
                      <XAxis dataKey="day" tick={{ fontSize: 9 }} hide />
                      <YAxis hide />
                      <ReTooltip
                        contentStyle={{ fontSize: 11, padding: 6 }}
                        formatter={(v) => [v, 'Rows']}
                        labelFormatter={(l) => `Day ${l}`}
                      />
                      <Line type="monotone" dataKey="rows" stroke="#6366f1" strokeWidth={2} dot={{ r: 2 }} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-xs text-muted-foreground italic">No runs yet</div>
                )}
              </div>
            </CardContent>
          </Card>
          <Card data-testid="spark-amount-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Amount · last 30 days</div>
                  <div className="text-3xl font-bold tabular-nums text-emerald-600" data-testid="spark-amount-total">{fmtSR(totalAmount)}</div>
                </div>
                <Badge variant="outline" className="text-[10px]">avg/run {fmtSR(sparkData.length ? totalAmount / sparkData.length : 0)}</Badge>
              </div>
              <div className="h-24">
                {sparkData.length > 0 ? (
                  <ResponsiveContainer>
                    <LineChart data={sparkData}>
                      <XAxis dataKey="day" tick={{ fontSize: 9 }} hide />
                      <YAxis hide />
                      <ReTooltip
                        contentStyle={{ fontSize: 11, padding: 6 }}
                        formatter={(v) => [fmtSR(v), 'Amount']}
                        labelFormatter={(l) => `Day ${l}`}
                      />
                      <Line type="monotone" dataKey="amount" stroke="#10b981" strokeWidth={2} dot={{ r: 2 }} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-xs text-muted-foreground italic">No runs yet</div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* iter509 — Auto-Suggested presets card. Surfaces description
            signatures with ≥ 3 un-linked rows that aren't covered by
            an existing preset, ranked by total amount. One-click
            Create opens a dialog pre-filled with the signature; one-
            click Dismiss hides forever. */}
        {suggestions.length > 0 && (
          <Card className="border-sky-200 bg-gradient-to-r from-sky-50/60 to-cyan-50/30" data-testid="suggestions-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <Lightbulb size={14} className="text-sky-600" />
                <h3 className="text-sm font-bold">Suggested presets · last 190 days</h3>
                <Badge variant="outline" className="text-[10px] border-sky-300 text-sky-700 ml-2" data-testid="suggestions-count">
                  {suggestions.length} pattern{suggestions.length === 1 ? '' : 's'} found
                </Badge>
                {/* iter513 — Bulk-create toolbar */}
                <div className="ml-auto flex items-center gap-1.5 flex-wrap">
                  <Button
                    size="sm" variant="outline"
                    className="h-7 text-[10px] rounded-full px-2.5"
                    onClick={selectAllVisible}
                    data-testid="bulk-select-all-btn"
                  >Select all</Button>
                  {bulkSelected.size > 0 && (
                    <Button
                      size="sm" variant="ghost"
                      className="h-7 text-[10px] rounded-full px-2.5 text-muted-foreground"
                      onClick={clearBulkSelected}
                      data-testid="bulk-clear-btn"
                    >Clear ({bulkSelected.size})</Button>
                  )}
                  <Button
                    size="sm"
                    className="h-7 text-[10px] bg-sky-600 hover:bg-sky-700 disabled:opacity-50"
                    disabled={bulkSelected.size === 0 || bulkBusy}
                    onClick={openBulkPanel}
                    data-testid="bulk-create-btn"
                  >
                    <Plus size={10} className="mr-1" />
                    Create {bulkSelected.size > 0 ? `${bulkSelected.size} selected` : 'selected'}
                  </Button>
                </div>
              </div>

              {/* iter513 — Inline batch defaults panel (kind + category for kind='expense') */}
              {bulkPanel && (
                <div className="rounded-lg border-2 border-sky-400 bg-sky-50 p-3 mb-3" data-testid="bulk-panel">
                  <div className="text-xs font-semibold text-sky-900 mb-2">
                    Batch create defaults — applied to all {bulkSelected.size} selected pattern{bulkSelected.size === 1 ? '' : 's'}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    <div>
                      <label className="text-[10px] text-muted-foreground uppercase tracking-wider">Kind</label>
                      <select
                        className="w-full h-8 text-xs border border-input rounded-md bg-white px-2"
                        value={bulkPanel.kind}
                        onChange={(e) => setBulkPanel((p) => ({ ...p, kind: e.target.value }))}
                        data-testid="bulk-kind-select"
                      >
                        <option value="expense">Expense</option>
                        <option value="advance">Pass-through advance</option>
                      </select>
                    </div>
                    {bulkPanel.kind === 'expense' && (
                      <div className="md:col-span-2">
                        <label className="text-[10px] text-muted-foreground uppercase tracking-wider">Category *</label>
                        <input
                          type="text"
                          className="w-full h-8 text-xs border border-input rounded-md bg-white px-2"
                          placeholder="e.g. bank_fees, salaries, rent"
                          value={bulkPanel.category}
                          onChange={(e) => setBulkPanel((p) => ({ ...p, category: e.target.value }))}
                          data-testid="bulk-category-input"
                        />
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-2.5">
                    <Button
                      size="sm"
                      className="h-7 text-[10px] bg-sky-600 hover:bg-sky-700"
                      onClick={submitBulkCreate}
                      disabled={bulkBusy}
                      data-testid="bulk-confirm-btn"
                    >
                      {bulkBusy ? 'Creating…' : `Create ${bulkSelected.size} preset${bulkSelected.size === 1 ? '' : 's'}`}
                    </Button>
                    <Button
                      size="sm" variant="ghost"
                      className="h-7 text-[10px] text-muted-foreground"
                      onClick={() => setBulkPanel(null)}
                      disabled={bulkBusy}
                      data-testid="bulk-cancel-btn"
                    >Cancel</Button>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {suggestions.slice(0, 12).map((sg) => {
                  const checked = bulkSelected.has(sg.signature);
                  return (
                  <div key={sg.signature} className={`rounded-lg border bg-white px-3 py-2.5 transition ${checked ? 'border-sky-500 ring-2 ring-sky-200' : 'border-sky-200'}`} data-testid={`suggestion-row-${sg.signature.replace(/\s+/g, '_')}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <input
                        type="checkbox"
                        className="h-3.5 w-3.5 rounded border-sky-300 accent-sky-600 cursor-pointer"
                        checked={checked}
                        onChange={() => toggleBulkSelected(sg.signature)}
                        data-testid={`suggestion-checkbox-${sg.signature.replace(/\s+/g, '_')}`}
                        title="Include in bulk-create"
                      />
                      <code className="text-[11px] font-mono bg-sky-100 px-1.5 py-0.5 rounded text-sky-900 truncate">{sg.signature}</code>
                      <Badge variant="outline" className="text-[10px]">{sg.count} rows</Badge>
                      <span className="text-xs font-bold tabular-nums text-emerald-700 ml-auto">{fmtSR(sg.total_amount)}</span>
                    </div>
                    <div className="text-[10px] text-muted-foreground truncate mb-1.5">
                      {(sg.samples?.[0]?.description || '').slice(0, 90)}
                      {sg.samples?.length > 1 ? ` · +${sg.samples.length - 1} more` : ''}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Button
                        size="sm"
                        className="h-7 text-[10px] bg-sky-600 hover:bg-sky-700"
                        onClick={() => openSuggestCreate(sg)}
                        data-testid={`suggestion-create-${sg.signature.replace(/\s+/g, '_')}`}
                      >
                        <Plus size={10} className="mr-1" /> Create preset
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0 text-muted-foreground"
                        onClick={() => dismissSuggestion(sg)}
                        data-testid={`suggestion-dismiss-${sg.signature.replace(/\s+/g, '_')}`}
                        title="Don't suggest this again"
                      >
                        <X size={11} />
                      </Button>
                      {sg.first_seen && (
                        <span className="text-[9px] text-muted-foreground ml-auto">{sg.first_seen} → {sg.last_seen}</span>
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>
              {suggestions.length > 12 && (
                <p className="text-[10px] text-muted-foreground mt-2 italic text-center">+{suggestions.length - 12} more patterns hidden — dismiss or create some to see them.</p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Presets (focus on zero-match) */}
        {presets.length === 0 ? (
          <Card data-testid="presets-empty">
            <CardContent className="p-6 text-center text-sm text-muted-foreground">
              You haven't saved any Smart Presets yet.
              {' '}<Link to="/pass-through-advances" className="text-indigo-600 underline">Open Pass-Through Advances</Link>
              {' '}to create one from a bank-statement suggestion.
            </CardContent>
          </Card>
        ) : (
          <Card data-testid="presets-grid">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles size={14} className="text-violet-600" />
                <h3 className="text-sm font-bold">Smart Presets · Last 120 days</h3>
                {zeroMatchPresetIds.size > 0 && (
                  <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700">
                    <AlertCircle size={10} className="mr-1" />
                    {zeroMatchPresetIds.size} matched 0 rows last run
                  </Badge>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="preset-health-table">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
                      <th className="text-left py-2 px-2">Preset</th>
                      <th className="text-right py-2 px-2">Runs</th>
                      <th className="text-right py-2 px-2">Active</th>
                      <th className="text-right py-2 px-2">Rows</th>
                      <th className="text-right py-2 px-2">Amount</th>
                      <th className="text-left py-2 px-2 w-28">Activity</th>
                      <th className="py-2 px-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {presets.map((p) => {
                      const h = (health.presets || []).find((x) => x.id === p.id);
                      const runsTotal = h?.runs_total || 0;
                      const runsActive = h?.runs_active || 0;
                      const ratio = runsTotal > 0 ? Math.round((runsActive / runsTotal) * 100) : 0;
                      const spark = h?.sparkline || [];
                      const sparkData = spark.map((v, i) => ({ d: i, v }));
                      const zero = zeroMatchPresetIds.has(p.id);
                      const dormant = runsTotal > 0 && runsActive === 0;
                      const ratioBadge = runsTotal === 0
                        ? { tone: 'muted', text: 'no runs' }
                        : dormant
                        ? { tone: 'rose', text: '0% dormant' }
                        : ratio >= 80
                        ? { tone: 'emerald', text: `${ratio}%` }
                        : ratio >= 50
                        ? { tone: 'amber', text: `${ratio}%` }
                        : { tone: 'rose', text: `${ratio}%` };
                      const toneCls = {
                        emerald: 'border-emerald-300 text-emerald-700',
                        amber: 'border-amber-300 text-amber-700',
                        rose: 'border-rose-300 text-rose-700',
                        muted: 'border-border text-muted-foreground',
                      }[ratioBadge.tone];
                      return (
                        <tr key={p.id} className={`border-b border-border/40 ${zero || dormant ? 'bg-amber-50/30' : ''}`} data-testid={`preset-card-${p.id}`}>
                          <td className="py-2 px-2">
                            <div className="font-semibold truncate max-w-[300px]" title={p.name}>{p.name}</div>
                            <div className="text-[10px] text-muted-foreground flex items-center gap-1 flex-wrap mt-0.5">
                              <Badge variant="outline" className="text-[9px] uppercase tracking-wider">{p.kind}</Badge>
                              <span>{p.match_by === 'signature' ? p.signature : p.person_link?.link_name}</span>
                            </div>
                          </td>
                          <td className="py-2 px-2 text-right tabular-nums" data-testid={`preset-runs-total-${p.id}`}>{runsTotal}</td>
                          <td className="py-2 px-2 text-right">
                            <Badge variant="outline" className={`text-[10px] tabular-nums ${toneCls}`} data-testid={`preset-health-ratio-${p.id}`}>{ratioBadge.text}</Badge>
                          </td>
                          <td className="py-2 px-2 text-right tabular-nums font-semibold" data-testid={`preset-rows-${p.id}`}>{(h?.rows || 0).toLocaleString()}</td>
                          <td className="py-2 px-2 text-right tabular-nums text-emerald-700" data-testid={`preset-amount-${p.id}`}>{fmtSR(h?.amount || 0)}</td>
                          <td className="py-2 px-2">
                            <div className="h-7 w-24" data-testid={`preset-spark-${p.id}`}>
                              {sparkData.some((d) => d.v > 0) ? (
                                <ResponsiveContainer>
                                  <BarChart data={sparkData}>
                                    <Bar dataKey="v" fill={ratioBadge.tone === 'emerald' ? '#10b981' : ratioBadge.tone === 'amber' ? '#f59e0b' : '#f43f5e'} />
                                    <ReTooltip contentStyle={{ fontSize: 10, padding: 4 }} formatter={(v) => [v, 'rows']} cursor={{ fill: '#0001' }} />
                                  </BarChart>
                                </ResponsiveContainer>
                              ) : (
                                <div className="h-full flex items-center text-[10px] text-muted-foreground italic">—</div>
                              )}
                            </div>
                          </td>
                          <td className="py-2 px-2 text-right">
                            <Button
                              size="sm"
                              variant="outline"
                              className={`h-7 text-[10px] ${zero || dormant ? 'border-amber-400 text-amber-700' : ''}`}
                              onClick={() => openEdit(p)}
                              data-testid={`preset-edit-${p.id}`}
                              title={dormant ? 'Dormant — refine signature to start matching rows' : zero ? 'This preset matched 0 rows in the last run' : 'Edit preset'}
                            >
                              <Edit size={10} className="mr-1" /> Edit
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recent runs */}
        <Card data-testid="runs-list-card">
          <CardContent className="p-0">
            <div className="px-4 py-3 border-b border-border">
              <h3 className="text-sm font-bold">Recent runs</h3>
              <p className="text-[11px] text-muted-foreground mt-0.5">Click any row to see per-preset breakdown.</p>
            </div>
            {loading ? (
              <div className="p-6 text-center text-sm text-muted-foreground">Loading…</div>
            ) : runs.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground">
                No runs yet. The cron fires daily at 09:00 — or click <strong>Run now</strong> above.
              </div>
            ) : (
              <div className="divide-y divide-border">
                {runs.map((run, i) => {
                  const key = `${run.ran_at}-${i}`;
                  const isOpen = !!expanded[key];
                  const zeroPresets = (run.per_preset || []).filter((p) => p.converted === 0).length;
                  return (
                    <div key={key} data-testid={`run-row-${i}`}>
                      <button
                        className="w-full px-4 py-3 flex items-center gap-3 hover:bg-muted/40 text-left"
                        onClick={() => setExpanded((s) => ({ ...s, [key]: !s[key] }))}
                        data-testid={`run-toggle-${i}`}
                      >
                        {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold">{fmtDate(run.ran_at)}</span>
                            <span className="text-[10px] text-muted-foreground">{(run.ran_at || '').slice(11, 16)} UTC</span>
                            {run._manual ? <Badge variant="outline" className="text-[9px] border-indigo-300 text-indigo-700">manual</Badge> : null}
                            {run.rows_converted > 0 ? (
                              <Badge variant="outline" className="text-[9px] border-emerald-300 text-emerald-700">
                                <Check size={9} className="mr-1" /> {run.rows_converted} rows · {fmtSR(run.total_amount)}
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-[9px]">0 rows</Badge>
                            )}
                            {zeroPresets > 0 && (
                              <Badge variant="outline" className="text-[9px] border-amber-300 text-amber-700">
                                {zeroPresets} preset{zeroPresets === 1 ? '' : 's'} matched 0
                              </Badge>
                            )}
                            {run.failed_presets?.length > 0 && (
                              <Badge variant="outline" className="text-[9px] border-rose-300 text-rose-700">
                                {run.failed_presets.length} failed
                              </Badge>
                            )}
                          </div>
                        </div>
                      </button>
                      {isOpen && (
                        <div className="px-4 pb-3 pt-1 bg-muted/20" data-testid={`run-detail-${i}`}>
                          {(run.per_preset || []).length === 0 ? (
                            <p className="text-xs text-muted-foreground italic">No presets ran.</p>
                          ) : (
                            <div className="space-y-1">
                              {(run.per_preset || []).map((pp, j) => (
                                <div key={pp.id || j} className="text-xs flex items-center gap-2 px-1">
                                  <span className={`tabular-nums w-8 text-right ${pp.converted > 0 ? 'text-emerald-600 font-semibold' : 'text-muted-foreground'}`}>{pp.converted}</span>
                                  <span className="flex-1 truncate">{pp.name}</span>
                                  <span className="text-muted-foreground tabular-nums">{fmtSR(pp.amount)}</span>
                                  {pp.converted === 0 && presets.find((x) => x.id === pp.id) && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-6 text-[10px] text-amber-700"
                                      onClick={() => openEdit(presets.find((x) => x.id === pp.id))}
                                      data-testid={`run-detail-edit-${pp.id}`}
                                    >
                                      <Edit size={10} className="mr-1" /> Refine
                                    </Button>
                                  )}
                                </div>
                              ))}
                              {(run.failed_presets || []).map((fp, j) => (
                                <div key={j} className="text-xs flex items-center gap-2 px-1 text-rose-700">
                                  <AlertCircle size={11} />
                                  <span className="flex-1 truncate">{fp.name}: {fp.error}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* iter509 — Create preset from suggestion dialog */}
        <Dialog open={!!suggestCreate} onOpenChange={(o) => { if (!o) setSuggestCreate(null); }}>
          <DialogContent className="sm:max-w-md" data-testid="suggest-create-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Lightbulb size={16} className="text-sky-600" /> Create preset from pattern
              </DialogTitle>
              {suggestCreate && (
                <DialogDescription>
                  Signature: <code className="text-[11px] font-mono bg-sky-100 px-1 rounded">{suggestCreate.signature}</code>
                  {' '}· {suggestCreate.count} matching rows · {fmtSR(suggestCreate.total_amount)}
                </DialogDescription>
              )}
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Preset name</Label>
                <Input
                  value={suggestForm.name}
                  onChange={(e) => setSuggestForm((f) => ({ ...f, name: e.target.value }))}
                  data-testid="suggest-create-name"
                />
              </div>
              <div>
                <Label>Target kind</Label>
                <Select value={suggestForm.kind} onValueChange={(v) => setSuggestForm((f) => ({ ...f, kind: v }))}>
                  <SelectTrigger data-testid="suggest-create-kind"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="expense">Expense</SelectItem>
                    <SelectItem value="supplier_payment">Supplier Payment</SelectItem>
                    <SelectItem value="salary_advance">Salary / Employee Advance</SelectItem>
                    <SelectItem value="partner_payment">Partner Draw</SelectItem>
                    <SelectItem value="advance">Pass-Through Advance</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {suggestForm.kind === 'expense' && (
                <div>
                  <Label>Default category *</Label>
                  <Input
                    value={suggestForm.category}
                    onChange={(e) => setSuggestForm((f) => ({ ...f, category: e.target.value }))}
                    placeholder="e.g. bank_fees, telecom, fuel"
                    data-testid="suggest-create-category"
                  />
                </div>
              )}
              {suggestForm.kind !== 'expense' && (
                <p className="text-[10px] text-amber-700 bg-amber-50 px-2 py-1.5 rounded">
                  Note: Supplier / Salary / Partner / Advance kinds need a default
                  party set in the preset library before they auto-apply. Create
                  the preset here, then open the Smart Presets library on
                  Pass-Through Advances to pick the default party.
                </p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSuggestCreate(null)} disabled={suggestBusy}>Cancel</Button>
              <Button
                onClick={submitSuggestCreate}
                disabled={suggestBusy || !suggestForm.name.trim() || (suggestForm.kind === 'expense' && !suggestForm.category.trim())}
                className="bg-sky-600 hover:bg-sky-700"
                data-testid="suggest-create-submit"
              >
                {suggestBusy ? 'Creating…' : 'Create preset'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Preset dialog */}
        <Dialog open={!!editPreset} onOpenChange={(o) => { if (!o) setEditPreset(null); }}>
          <DialogContent className="sm:max-w-md" data-testid="edit-preset-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit size={16} /> Edit Preset
              </DialogTitle>
              {editPreset && (
                <DialogDescription>
                  {editPreset.kind} · {editPreset.match_by}
                  {editPreset.match_by === 'person' && editPreset.person_link?.link_name ? ` · ${editPreset.person_link.link_name}` : null}
                </DialogDescription>
              )}
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Preset name</Label>
                <Input
                  value={editForm.name}
                  onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                  data-testid="edit-preset-name"
                />
              </div>
              {editPreset?.match_by === 'signature' && (
                <div>
                  <Label>Signature (first 2 description tokens, uppercase)</Label>
                  <Input
                    value={editForm.signature}
                    onChange={(e) => setEditForm((f) => ({ ...f, signature: e.target.value.toUpperCase() }))}
                    placeholder="e.g. SARIE OUTGOING"
                    data-testid="edit-preset-signature"
                  />
                  <p className="text-[10px] text-muted-foreground mt-1">Tip: if this preset matched 0 rows, the signature may be too specific. Try shorter / generic prefixes.</p>
                </div>
              )}
              {editPreset?.kind === 'expense' && (
                <div>
                  <Label>Default category</Label>
                  <Input
                    value={editForm.category}
                    onChange={(e) => setEditForm((f) => ({ ...f, category: e.target.value }))}
                    data-testid="edit-preset-category"
                  />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditPreset(null)} disabled={editBusy}>Cancel</Button>
              <Button
                onClick={submitEdit}
                disabled={editBusy || !editForm.name.trim()}
                data-testid="edit-preset-submit"
              >
                {editBusy ? 'Saving…' : 'Save changes'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
